import * as Yup from "yup";

/* -------------------------------------------------------------------------- */
/*                         CONFIG HELPER FUNCTIONS                            */
/* -------------------------------------------------------------------------- */

const getFieldConfig = (context: any, section: "fields" | "request", fieldName: string) => {
    return context?.serviceRequestConfig?.[section]?.[fieldName] || {};
};

const isFieldVisible = (context: any, section: "fields" | "request", fieldName: string) => {
    const config = getFieldConfig(context, section, fieldName);

    // If no configuration exists, preserve old behaviour and show the field.
    return config.visible !== false;
};

const isFieldRequired = (context: any, section: "fields" | "request", fieldName: string) => {
    const config = getFieldConfig(context, section, fieldName);

    return config.visible !== false && config.required === true;
};

const hasValue = (value: any) => {
    if (value === null || value === undefined) return false;

    if (typeof value === "string") {
        return value.trim().length > 0;
    }

    if (typeof value === "object") {
        return Object.keys(value).length > 0;
    }

    return true;
};

/* -------------------------------------------------------------------------- */
/*                         SUBJECT VALIDATION                                 */
/* -------------------------------------------------------------------------- */

export const serviceRequestValidations = Yup.object().shape({
    profile: Yup.object().shape({
        name_eng: Yup.string().test("name-required", "Name is required", function (value) {
            const context = this.options.context || {};

            if (!isFieldVisible(context, "fields", "name")) {
                return true;
            }

            if (!isFieldRequired(context, "fields", "name")) {
                return true;
            }

            return !!value?.trim();
        }),

        relation_name_eng: Yup.string().test(
            "relation-required",
            "Relative name is required",
            function (value) {
                const context = this.options.context || {};
                const { projectid } = context;

                if (!isFieldVisible(context, "fields", "relativeName")) {
                    return true;
                }

                if (projectid === "621") {
                    return true;
                }

                if (!isFieldRequired(context, "fields", "relativeName")) {
                    return true;
                }

                return !!value?.trim();
            }
        ),

        voterid: Yup.string().test("voterid-required", "Voter ID is required", function (value) {
            const context = this.options.context || {};
            const { projectid } = context;

            if (!isFieldVisible(context, "fields", "voterId")) {
                return true;
            }

            if (projectid === "621") {
                return true;
            }

            if (!isFieldRequired(context, "fields", "voterId")) {
                return true;
            }

            return !!value?.trim();
        }),

        request_level: Yup.string().test(
            "service-request-level-required",
            "Service request level is required",
            function (value) {
                const context = this.options.context || {};

                if (!isFieldVisible(context, "fields", "serviceRequestLevel")) {
                    return true;
                }

                if (!isFieldRequired(context, "fields", "serviceRequestLevel")) {
                    return true;
                }

                return !!value?.trim();
            }
        ),

        gender: Yup.string().test("gender-required", "Gender is required", function (value) {
            const context = this.options.context || {};

            if (!isFieldVisible(context, "fields", "gender")) {
                return true;
            }

            if (!isFieldRequired(context, "fields", "gender")) {
                return true;
            }

            return !!value?.trim();
        }),

        age: Yup.string().test("age-required", "Age is required", function (value) {
            const context = this.options.context || {};

            if (!isFieldVisible(context, "fields", "age")) {
                return true;
            }

            if (!isFieldRequired(context, "fields", "age")) {
                return true;
            }

            return !!value;
        }),

        address: Yup.string().test("address-required", "Address is required", function (value) {
            const context = this.options.context || {};
            const { projectid } = context;

            if (!isFieldVisible(context, "fields", "address")) {
                return true;
            }

            if (projectid === "621") {
                return true;
            }

            if (!isFieldRequired(context, "fields", "address")) {
                return true;
            }

            return !!value?.trim();
        }),
    }),

    additional: Yup.object().shape({
        booth: Yup.mixed().test("booth-required", "Please select Booth/Village", function (value) {
            const context = this.options.context || {};

            if (!isFieldVisible(context, "fields", "booth")) {
                return true;
            }

            if (!isFieldRequired(context, "fields", "booth")) {
                return true;
            }

            return hasValue(value);
        }),

        family_members_list: Yup.mixed().test(
            "family-required",
            "Add at least one family member",
            function () {
                const context = this.options.context || {};

                if (!isFieldVisible(context, "fields", "familyMembers")) {
                    return true;
                }

                if (!isFieldRequired(context, "fields", "familyMembers")) {
                    return true;
                }

                return Number(context.familyMemberCount || 0) > 0;
            }
        ),

        village: Yup.mixed(),

        is_outsider: Yup.boolean(),

        ac_name: Yup.string(),

        oacv_name: Yup.string(),

        oacv_phone: Yup.string(),
    }),

    dob: Yup.string().test("dob-required", "Date of birth is required", function (value) {
        const context = this.options.context || {};

        if (!isFieldVisible(context, "fields", "dob")) {
            return true;
        }

        if (!isFieldRequired(context, "fields", "dob")) {
            return true;
        }

        return !!value;
    }),

    phoneNotAvailable: Yup.boolean(),

    sameAsPhone: Yup.boolean(),

    phone: Yup.string().test("phone-required", "Enter valid 10 digit Phone number", function (value) {
        const context = this.options.context || {};
        const { projectid } = context;
        const { phoneNotAvailable } = this.parent;

        if (!isFieldVisible(context, "fields", "phone")) {
            return true;
        }

        if (projectid === "621") {
            return true;
        }

        if (phoneNotAvailable) {
            return true;
        }

        // Field is visible but not required.
        // If user enters something, validate it.
        if (!isFieldRequired(context, "fields", "phone")) {
            if (!value) return true;

            return /^(?:\+91|91)?[6-9][0-9]{9}$/.test(value);
        }

        if (!value) {
            return false;
        }

        return /^(?:\+91|91)?[6-9][0-9]{9}$/.test(value);
    }),

    whatsapp: Yup.string().test(
        "whatsapp-required",
        "Enter valid 10 digit Whatsapp number",
        function (value) {
            const context = this.options.context || {};
            const { phone, sameAsPhone, phoneNotAvailable } = this.parent;

            if (!isFieldVisible(context, "fields", "whatsapp")) {
                return true;
            }

            if (phoneNotAvailable) {
                return true;
            }

            if (!value) {
                return isFieldRequired(context, "fields", "whatsapp") ? false : true;
            }

            const isValid = /^(?:\+91|91)?[6-9][0-9]{9}$/.test(value);

            if (!isValid) {
                return false;
            }

            return true;
        }
    ),

    aadharid: Yup.string().test("aadhar-required", "Aadhaar ID is required", function (value) {
        const context = this.options.context || {};
        const { projectid } = context;

        if (!isFieldVisible(context, "fields", "aadhaar")) {
            return true;
        }

        if (projectid === "621") {
            return true;
        }

        if (!isFieldRequired(context, "fields", "aadhaar")) {
            return true;
        }

        return !!value?.trim();
    }),

    caste: Yup.mixed().test("caste-required", "Caste is required", function (value) {
        const context = this.options.context || {};

        if (!isFieldVisible(context, "fields", "caste")) {
            return true;
        }

        if (!isFieldRequired(context, "fields", "caste")) {
            return true;
        }

        return hasValue(value);
    }),

    profile_photo: Yup.mixed().test("camera-required", "Voter photo is required", function () {
        const context = this.options.context || {};

        if (!isFieldVisible(context, "fields", "camera")) {
            return true;
        }

        if (!isFieldRequired(context, "fields", "camera")) {
            return true;
        }

        return !!context.profilePhoto;
    }),
});

/* -------------------------------------------------------------------------- */
/*                       SERVICE REQUEST VALIDATION                           */
/* -------------------------------------------------------------------------- */

export const requestValidations = Yup.object().shape({
    status: Yup.mixed().test("status-required", "Select the status", function (value) {
        const context = this.options.context || {};

        if (!isFieldVisible(context, "request", "status")) {
            return true;
        }

        if (!isFieldRequired(context, "request", "status")) {
            return true;
        }

        return hasValue(value);
    }),

    priority: Yup.mixed().test("priority-required", "Select the priority", function (value) {
        const context = this.options.context || {};

        if (!isFieldVisible(context, "request", "priority")) {
            return true;
        }

        if (!isFieldRequired(context, "request", "priority")) {
            return true;
        }

        return hasValue(value);
    }),

    assignee: Yup.mixed().test("assignee-required", "Select the Assignee", function (value) {
        const context = this.options.context || {};

        if (!isFieldVisible(context, "request", "assignee")) {
            return true;
        }

        if (!isFieldRequired(context, "request", "assignee")) {
            return true;
        }

        return hasValue(value);
    }),

    location: Yup.mixed().test("location-required", "Select the location", function (value) {
        const context = this.options.context || {};

        if (!isFieldVisible(context, "request", "location")) {
            return true;
        }

        if (!isFieldRequired(context, "request", "location")) {
            return true;
        }

        return hasValue(value);
    }),

    secondary_assignee: Yup.mixed().test(
        "secondary-assignee-required",
        "Select the secondary assignee",
        function (value) {
            const context = this.options.context || {};

            if (!isFieldVisible(context, "request", "secondary_assignee")) {
                return true;
            }

            if (!isFieldRequired(context, "request", "secondary_assignee")) {
                return true;
            }

            return hasValue(value);
        }
    ),

    type: Yup.object()
        .nullable()
        .test("type-required", "Select the request type", function (value) {
            const context = this.options.context || {};

            if (!isFieldVisible(context, "request", "serviceRequestType")) {
                return true;
            }

            if (!isFieldRequired(context, "request", "serviceRequestType")) {
                return true;
            }

            return !!value && Object.keys(value).length > 0;
        }),

    more: Yup.object().shape({
        additional: Yup.string().test(
            "additional-required",
            "Additional information is required",
            function (value) {
                const context = this.options.context || {};

                if (!isFieldVisible(context, "request", "additionalInformation")) {
                    return true;
                }

                if (!isFieldRequired(context, "request", "additionalInformation")) {
                    return true;
                }

                return !!value?.trim();
            }
        ),

        attachments: Yup.mixed().test("attachments-required", "Attachment is required", function (value) {
            const context = this.options.context || {};

            if (!isFieldVisible(context, "request", "attachments")) {
                return true;
            }

            if (!isFieldRequired(context, "request", "attachments")) {
                return true;
            }

            return Array.isArray(value) && value.length > 0;
        }),

        ministry: Yup.mixed().test("ministry-required", "Select the ministry", function (value) {
            const context = this.options.context || {};

            if (!isFieldVisible(context, "request", "ministry")) {
                return true;
            }

            if (!isFieldRequired(context, "request", "ministry")) {
                return true;
            }

            return hasValue(value);
        }),

        assembly: Yup.mixed()
            .nullable()
            .test("assembly-required", "Select the assembly", function (value) {
                const context = this.options.context || {};

                if (!isFieldVisible(context, "request", "assembly")) {
                    return true;
                }

                if (!isFieldRequired(context, "request", "assembly")) {
                    return true;
                }

                return hasValue(value);
            }),

        requested_date: Yup.string().test(
            "requested-date-required",
            "Requested date is required",
            function (value) {
                const context = this.options.context || {};

                if (!isFieldVisible(context, "request", "requestedDate")) {
                    return true;
                }

                if (!isFieldRequired(context, "request", "requestedDate")) {
                    return true;
                }

                return !!value;
            }
        ),

        is_fund_required: Yup.mixed().test("funds-required", "Funds information is required", function () {
            const context = this.options.context || {};

            if (!isFieldVisible(context, "request", "funds")) {
                return true;
            }

            if (!isFieldRequired(context, "request", "funds")) {
                return true;
            }

            const { is_fund_required, funds_type, funds_amount } = this.parent || {};

            return is_fund_required === true && hasValue(funds_type) && hasValue(funds_amount);
        }),

        is_rtc_required: Yup.mixed().test("rtc-required", "RTC information is required", function () {
            const context = this.options.context || {};

            if (!isFieldVisible(context, "request", "rtc")) {
                return true;
            }

            if (!isFieldRequired(context, "request", "rtc")) {
                return true;
            }

            const { is_rtc_required, rtc_details } = this.parent || {};

            return is_rtc_required === true && !!String(rtc_details || "").trim();
        }),
    }),
});
